"use client";

import React from 'react';
import styles from"../css/Status.module.css";

const Status = ({ 
  status, 
  variant = 'default',
  className = '',
  tooltip,
  enableHover = false,
  tone,
  ...props 
}) => {
  // Map status values to CSS classes
  const getStatusClass = (statusValue) => {
    // Handle null, undefined, or non-string values
    if (!statusValue || typeof statusValue !== 'string') {
      return styles.default;
    }
    
    const normalizedStatus = statusValue.toLowerCase().trim();
    // Normalize common domain-specific statuses
    if (normalizedStatus.includes('ready')) {
      // e.g., "Ready To Collect" -> green
      return styles.completed;
    }
    // Domain mappings
    if (normalizedStatus.includes('approved') && !normalizedStatus.includes('partially')) {
      return styles.completed; // green
    }
    if (normalizedStatus === 'partially approved') return styles.pending; // amber/yellow
    if (normalizedStatus === 'declined') return styles.cancelled; // red
    if (normalizedStatus === 'rejected') return styles.cancelled; // red (alias)
    
    // Stock status variants
    if (variant === 'stock') {
      switch (normalizedStatus) {
        case 'in stock':
          return styles.inStock;
        case 'low stock':
          return styles.lowStock;
        case 'out of stock':
          return styles.outOfStock;
        default:
          return styles.default;
      }
    }
    
    // Priority status variants
    if (variant === 'priority') {
      switch (normalizedStatus) {
        case 'high':
          return styles.high;
        case 'moderate':
          return styles.moderate;
        case 'low':
          return styles.low;
        case 'expired':
          return styles.expired;
        default:
          return styles.default;
      }
    }
    
    // Order/Process status variants
    if (variant === 'process') {
      switch (normalizedStatus) {
        case 'pending':
          return styles.pending;
        case 'processing':
          return styles.processing;
        case 'completed':
          return styles.completed;
        case 'cancelled':
          return styles.cancelled;
        default:
          return styles.default;
      }
    }
    
    // Default auto-detection based on common status values
    switch (normalizedStatus) {
      case 'in stock':
        return styles.inStock;
      case 'low stock':
        return styles.lowStock;
      case 'out of stock':
        return styles.outOfStock;
      case 'high':
        return styles.high;
      case 'moderate':
        return styles.moderate;
      case 'low':
        return styles.low;
      case 'expired':
        return styles.expired;
      case 'pending':
        return styles.pending;
      case 'processing':
        return styles.processing;
      case 'completed':
        return styles.completed;
      case 'cancelled':
        return styles.cancelled;
      default:
        return styles.default;
    }
  };

  let statusClass = getStatusClass(status);
  if (tone) {
    const t = String(tone).toLowerCase().trim();
    if (t === 'high' || t === 'red') statusClass = styles.high;
    else if (t === 'moderate' || t === 'amber' || t === 'yellow') statusClass = styles.moderate;
    else if (t === 'low' || t === 'green') statusClass = styles.low;
  }

  // Tooltip messaging per status/variant
  const normalized = (status || '').toString().toLowerCase().trim();
  let tooltipMsg = '';
  if (variant === 'priority') {
    if (normalized === 'expired') tooltipMsg = 'Expired item';
    else if (normalized === 'high') tooltipMsg = 'High priority';
    else if (normalized === 'moderate') tooltipMsg = 'Moderate priority';
    else if (normalized === 'low') tooltipMsg = 'Low priority';
  } else if (variant === 'stock') {
    if (normalized === 'out of stock') tooltipMsg = 'Out of stock';
    else if (normalized === 'low stock') tooltipMsg = 'Low stock';
    else if (normalized === 'in stock') tooltipMsg = 'In stock';
  }
  // Default helpful fallback
  if (tooltip && typeof tooltip === 'string') {
    tooltipMsg = tooltip;
  } else if (!tooltipMsg) {
    tooltipMsg = status;
  }

  // Tooltip color should match badge color theme
  // Map the same class used on badge to a tooltip theme class
  const tooltipTheme = statusClass; // CSS defines matching background/text per theme

  const wrapperClass = [styles.badgeWrap, enableHover ? styles.hoverable : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapperClass} {...props}>
      <span className={`${styles.badge} ${statusClass}`.trim()}>{status}</span>
      {enableHover && (
        <span className={`${styles.tooltip} ${tooltipTheme}`.trim()} role="tooltip">
          {tooltipMsg}
        </span>
      )}
    </span>
  );
};

export default Status;
